/**
 * useDialog360 — admin-console hook for the full 360dialog management surface
 * (backend route prefix api/dialog360/*, served by CCMS.Api.Communications).
 *
 * Uses the SDK's raw HttpClient + getApiUrl (the established fallback pattern on
 * this page) so it needs no SDK rebuild. JSON-only; media file upload/download is
 * handled by the existing Media tab / direct links.
 */

import { useState, useCallback } from 'react';
import { useHttpClient } from '@actschurch/ccms-sdk';
import type { AppWhatsAppMessage } from '@actschurch/ccms-sdk';
import { toast } from '../../../../../components/ui/providers/AlertProvider';
import { getApiUrl } from '../../../../../config/ccms';
import type {
  Dialog360ConnectionResult,
  BusinessProfile,
  PhoneNumberInfo,
  AppWhatsAppInboundMessage,
  AppWhatsAppOutboundMessage,
  TemplateListItem,
  TemplateMutationResponse,
  CreateTemplateApiRequest,
  EditTemplateApiRequest,
  Dialog360Billing,
  Dialog360Config,
  SendTextReq,
  SendTemplateReq,
  SendMediaReq,
  SendLocationReq,
  SendContactsReq,
  SendInteractiveButtonsReq,
  SendInteractiveListReq,
  SendCtaUrlReq,
  SendReactionReq,
} from '../dialog360.types';

const path = (p: string) => getApiUrl('communications', `/api/dialog360${p}`);

export function useDialog360() {
  const httpClient = useHttpClient();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [connectionResult, setConnectionResult] = useState<Dialog360ConnectionResult | null>(null);
  const [profile, setProfile] = useState<BusinessProfile | null>(null);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumberInfo[]>([]);
  const [inbound, setInbound] = useState<AppWhatsAppInboundMessage[]>([]);
  const [messages, setMessages] = useState<AppWhatsAppOutboundMessage[]>([]);
  const [cloudTemplates, setCloudTemplates] = useState<TemplateListItem[]>([]);
  const [billing, setBilling] = useState<Dialog360Billing | null>(null);
  const [config, setConfig] = useState<Dialog360Config | null>(null);

  // ---- Diagnostics ---------------------------------------------------------

  const testConnection = useCallback(async (): Promise<Dialog360ConnectionResult | null> => {
    setLoading(true);
    setError(null);
    try {
      const resp = await httpClient.get<Dialog360ConnectionResult>(path('/diagnostics/test-connection'));
      const result = (resp.data as Dialog360ConnectionResult) ?? null;
      setConnectionResult(result);
      return result;
    } catch (err: any) {
      const msg = err?.message || 'Failed to test 360dialog connection';
      setError(msg);
      const failed = { success: false, error: msg } as Dialog360ConnectionResult;
      setConnectionResult(failed);
      return failed;
    } finally {
      setLoading(false);
    }
  }, [httpClient]);

  // ---- Profile / phone numbers --------------------------------------------

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await httpClient.get<{ data?: BusinessProfile[] }>(path('/profile'));
      setProfile(resp.data?.data?.[0] ?? null);
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch business profile');
    } finally {
      setLoading(false);
    }
  }, [httpClient]);

  const updateProfile = useCallback(async (data: BusinessProfile) => {
    await toast.promise(httpClient.put(path('/profile'), data), {
      loading: 'Updating business profile...',
      success: 'Business profile updated',
      error: (e: Error) => `Failed to update profile: ${e.message}`,
    });
    await fetchProfile();
  }, [httpClient, fetchProfile]);

  const fetchPhoneNumbers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await httpClient.get<{ data?: PhoneNumberInfo[] }>(path('/profile/phone-numbers'));
      setPhoneNumbers(resp.data?.data ?? []);
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch phone numbers');
    } finally {
      setLoading(false);
    }
  }, [httpClient]);

  // ---- Inbound -------------------------------------------------------------

  const fetchInbound = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await httpClient.get<AppWhatsAppInboundMessage[]>(path('/inbound'));
      setInbound(resp.data ?? []);
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch inbound messages');
    } finally {
      setLoading(false);
    }
  }, [httpClient]);

  // ---- Outbound messages (queue + sent history) ---------------------------

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await httpClient.get<AppWhatsAppOutboundMessage[]>(path('/messages'));
      setMessages(resp.data ?? []);
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch outbound messages');
    } finally {
      setLoading(false);
    }
  }, [httpClient]);

  // ---- Cloud (360dialog) templates ----------------------------------------

  const fetchCloudTemplates = useCallback(async (limit = 1000, offset = 0, sort = 'business_templates.name') => {
    setLoading(true);
    setError(null);
    try {
      const resp = await httpClient.get<{ templates?: TemplateListItem[] }>(
        path(`/templates?limit=${limit}&offset=${offset}&sort=${encodeURIComponent(sort)}`),
      );
      setCloudTemplates(resp.data?.templates ?? []);
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch 360dialog templates');
    } finally {
      setLoading(false);
    }
  }, [httpClient]);

  const createCloudTemplate = useCallback(async (req: CreateTemplateApiRequest) => {
    await toast.promise(httpClient.post<TemplateMutationResponse>(path('/templates'), req), {
      loading: 'Submitting template for approval...',
      success: 'Template submitted to 360dialog',
      error: (e: Error) => `Failed to create template: ${e.message}`,
    });
    await fetchCloudTemplates();
  }, [httpClient, fetchCloudTemplates]);

  const editCloudTemplate = useCallback(async (externalId: string, req: EditTemplateApiRequest) => {
    await toast.promise(
      httpClient.post<TemplateMutationResponse>(path(`/templates/${encodeURIComponent(externalId)}`), req),
      {
        loading: 'Updating template...',
        success: 'Template updated',
        error: (e: Error) => `Failed to update template: ${e.message}`,
      },
    );
    await fetchCloudTemplates();
  }, [httpClient, fetchCloudTemplates]);

  const deleteCloudTemplate = useCallback(async (name: string) => {
    await toast.promise(
      httpClient.delete<TemplateMutationResponse>(path(`/templates/${encodeURIComponent(name)}`)),
      {
        loading: 'Deleting template...',
        success: 'Template deleted',
        error: (e: Error) => `Failed to delete template: ${e.message}`,
      },
    );
    await fetchCloudTemplates();
  }, [httpClient, fetchCloudTemplates]);

  // ---- Billing / usage -----------------------------------------------------

  const fetchBilling = useCallback(async (days = 30) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await httpClient.get<Dialog360Billing>(path(`/billing?days=${days}`));
      setBilling((resp.data as Dialog360Billing) ?? null);
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch billing data');
      setBilling(null);
    } finally {
      setLoading(false);
    }
  }, [httpClient]);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await httpClient.get<Dialog360Config>(path('/config'));
      setConfig((resp.data as Dialog360Config) ?? null);
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch configuration');
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, [httpClient]);

  // ---- Send (api/dialog360/send/*) ----------------------------------------

  const send = useCallback(
    async <T>(endpoint: string, body: T, label: string): Promise<AppWhatsAppMessage | null> => {
      const resp = await toast.promise(
        httpClient.post<AppWhatsAppMessage>(path(`/send/${endpoint}`), body),
        {
          loading: `Sending ${label}...`,
          success: `${label} sent`,
          error: (e: Error) => `Failed to send ${label}: ${e.message}`,
        },
      );
      return (resp.data as AppWhatsAppMessage) ?? null;
    },
    [httpClient],
  );

  const sendText = useCallback((r: SendTextReq) => send('text', r, 'text message'), [send]);
  const sendTemplate = useCallback((r: SendTemplateReq) => send('template', r, 'template message'), [send]);
  const sendImage = useCallback((r: SendMediaReq) => send('image', r, 'image'), [send]);
  const sendVideo = useCallback((r: SendMediaReq) => send('video', r, 'video'), [send]);
  const sendAudio = useCallback((r: SendMediaReq) => send('audio', r, 'audio'), [send]);
  const sendDocument = useCallback((r: SendMediaReq) => send('document', r, 'document'), [send]);
  const sendSticker = useCallback((r: SendMediaReq) => send('sticker', r, 'sticker'), [send]);
  const sendLocation = useCallback((r: SendLocationReq) => send('location', r, 'location'), [send]);
  const sendContacts = useCallback((r: SendContactsReq) => send('contacts', r, 'contacts'), [send]);
  const sendButtons = useCallback((r: SendInteractiveButtonsReq) => send('interactive/buttons', r, 'interactive buttons'), [send]);
  const sendList = useCallback((r: SendInteractiveListReq) => send('interactive/list', r, 'interactive list'), [send]);
  const sendCtaUrl = useCallback((r: SendCtaUrlReq) => send('interactive/cta-url', r, 'CTA URL message'), [send]);
  const sendReaction = useCallback((r: SendReactionReq) => send('reaction', r, 'reaction'), [send]);

  return {
    loading,
    error,
    setError,
    connectionResult,
    profile,
    phoneNumbers,
    inbound,
    messages,
    cloudTemplates,
    billing,
    config,
    // diagnostics
    testConnection,
    // profile
    fetchProfile,
    updateProfile,
    fetchPhoneNumbers,
    // inbound
    fetchInbound,
    // outbound
    fetchMessages,
    // cloud templates
    fetchCloudTemplates,
    createCloudTemplate,
    editCloudTemplate,
    deleteCloudTemplate,
    // billing
    fetchBilling,
    // config
    fetchConfig,
    // send
    sendText,
    sendTemplate,
    sendImage,
    sendVideo,
    sendAudio,
    sendDocument,
    sendSticker,
    sendLocation,
    sendContacts,
    sendButtons,
    sendList,
    sendCtaUrl,
    sendReaction,
  };
}
